// Offers the check-out (SPEC F10 base, DESIGN_SYSTEM §5): the next time you open the app or a project after a session
// ended, at most once per session, and never on Dig, where nothing may cover the player (it waits for the next
// screen). Always skippable, never blocking: it is one lazy sheet and nothing else waits for it. It never opens over
// another sheet: with one open, the offer waits until that sheet closes (audit A6).
import { lazy, Suspense, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router';
import { useAppStore } from '../../app/store';
import type { Session } from '../../data/types';
import { recentSessions } from '../../db/repos/sessions';
import { checkoutCandidate } from '../../lib/journal';
import { openSheetCount, subscribeSheets } from '../../ui/openSheets';
import { currentSessionIds, startJournal, subscribeForeground } from './journalStore';
import { useProjects } from './projectsStore';

const loadSheet = () => import('./CheckoutSheet');
const CheckoutSheet = lazy(loadSheet);

const onDig = (path: string): boolean => path === '/dig' || path.startsWith('/dig/') || path.startsWith('/dig?');
const onProject = (path: string): boolean => /^\/beat\/[^/?]+/.test(path);

export function CheckoutHost() {
  const { pathname } = useLocation();
  const hydrated = useAppStore((s) => s.hydrated);
  const [session, setSession] = useState<Session>();
  const sheets = useSyncExternalStore(subscribeSheets, openSheetCount);
  const [shownId, setShownId] = useState<string>();
  const waiting = useRef(false);
  const where = useRef(pathname);
  const mounted = useRef(true);
  useEffect(() => {
    where.current = pathname;
  });

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // The journal watches the page's visibility for as long as the app is open
  useEffect(() => startJournal(), []);

  const evaluate = useCallback(async () => {
    // Dig keeps the player uncovered: the offer waits for the next screen
    if (onDig(where.current)) {
      waiting.current = true;
      return;
    }
    waiting.current = false;
    if (!useProjects.getState().projects) await useProjects.getState().load();
    if (!mounted.current) return;
    const alive = (id: string) => Boolean(useProjects.getState().projects?.some((p) => p.id === id));
    const next = checkoutCandidate(await recentSessions(), {
      seen: useAppStore.getState().continuity.checkoutSeen,
      current: currentSessionIds(),
      alive,
    });
    // Load the sheet before the offer is made: a sheet still loading is not yet counted as open, and a tap in that
    // gap would open another one under it (audit A6)
    if (next) await loadSheet();
    if (mounted.current) setSession((cur) => cur ?? next); // not after an unmount: the host may be gone with its window
  }, []);

  // The app opens, and the app comes back to the foreground
  useEffect(() => {
    if (!hydrated) return;
    void evaluate();
    return subscribeForeground(() => void evaluate());
  }, [hydrated, evaluate]);

  // A project opens, or Dig is left with an offer waiting
  useEffect(() => {
    if (!hydrated) return;
    if (onProject(pathname) || (waiting.current && !onDig(pathname))) void evaluate();
  }, [pathname, hydrated, evaluate]);

  // Once shown it stays (it counts itself among the open sheets); a new offer starts waiting again
  if (session && shownId !== session.id && sheets === 0) setShownId(session.id);
  if (!session || shownId !== session.id) return null;
  return (
    <Suspense fallback={null}>
      <CheckoutSheet key={session.id} session={session} onClose={() => setSession(undefined)} />
    </Suspense>
  );
}
